import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getGameDetails } from '../../apiservices/LocalStorageService';
import {
  fetchNextQuestion,
  submitQuestion,
  fetchGameStatusStream,
} from '../../apiservices/axiosservice/gameservice';
import { useSse } from '../../sse';
import { getCurrentCounter } from '../../utils/timeCounter';
import PlayerGameOver from './PlayerGameOver';
import './PlayerFlow.css';
import MusicToggle from '../gamelobby/MusicToggle';
import { toast } from 'react-toastify';
import { sseManager } from '../../sse';
import Confetti from '../animations/Confetti';
import { useLeaderboard } from '../../hooks/useLeaderboard';

interface Option {
  optionId: string;
  answerText: string;
  color?: string;
}
interface Question {
  questionId: string;
  questionMessage: string;
  options: Option[];
  timeInterval?: number;
  optionType: string;
  answerCount: number;
}

const optionLetters = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
const podiumColors = ['#8B8B8B', '#D4A843', '#8B7355'];

const PlayerQuizPlay: React.FC = () => {
  const navigate = useNavigate();
  const gd = getGameDetails();
  const { gameId, playerId, scheduledGame, totalQuestion } = gd || {};

  const [question, setQuestion] = useState<Question | undefined>();
  const [quizPosition, setQuizPosition] = useState(1);
  const [timeLeft, setTimeLeft] = useState<number>(20);
  const [selected, setSelected] = useState<string[]>([]);
  const [revealed, setRevealed] = useState(false);
  const [correctIds, setCorrectIds] = useState<string[]>(['a']); // mock correct
  const [correctAnswers, setCorrectAnswers] = useState<string[]>([]);
  const [gameOver, setGameOver] = useState(false);
  const [playersAnswered, setPlayersAnswered] = useState(4);
  const submittedRef = useRef(false);
  const [showConfetti, setShowConfetti] = useState(false);

  const quizSync = useSse<any>('quiz-sync');
  const quizResult = useSse<any>('quiz-result');
  const gameStatus = useSse<GameStatusInfo>('game-status');
  const [gameStatusData, setGameStatusData] = useState<any>(null);
  const leaderboardEntry = useLeaderboard(gameId);
  const isAnswerCorrect = useRef<boolean>(false);
  const [quizResultData, setQuizResultData] = useState();

  // Check Overall Correctness of Answer
  const isOverallCorrect = (
    options: Option[],
    selectedIds: string[],
    answers: string[]
  ) => {
    const selectedAnswerTexts = options
      .filter((option) => selectedIds.includes(option.optionId))
      .map((option) => option.answerText);
    return answers.every((answer) => selectedAnswerTexts.includes(answer));
  };

  // Initiate connection to SSE
  useEffect(() => {
    if (gameId) sseManager.connect(gameId);
  }, [gameId]);
  // SSE: new question from host
  useEffect(() => {
    if (quizSync?.nextQuestion) {
      setQuestion(quizSync.nextQuestion);
      setQuizPosition(quizSync.quizPosition);
      setTimeLeft(getCurrentCounter(quizSync.currentExpiryTime));
      setSelected([]);
      setRevealed(false);
      setCorrectIds([]);
      setQuizResultData(undefined);
      submittedRef.current = false;
      isAnswerCorrect.current = false;
      setShowConfetti(false);
    }
  }, [quizSync]);

  // SSE: answers revealed
  useEffect(() => {
    if (quizResult) {
      setRevealed(true);
      setCorrectIds(quizResult.answers || ['a']);
      setQuizResultData(quizResult);
      if (question) {
        isAnswerCorrect.current = isOverallCorrect(
          question.options,
          selected,
          quizResult.answers
        );
      }
    }
  }, [quizResult]);

  //

  // SSE: game finished
  useEffect(() => {
    setGameStatusData(gameStatus);

    if (gameStatus?.status === 'FINISHED' && leaderboardEntry.length > 0)
      setGameOver(true);
  }, [gameStatus]);

  // Non-scheduled: fetch first question
  useEffect(() => {
    if (gameId && playerId) {
      fetchNextQuestion({ gameId, playerId })
        .then((res) => {
          const d = res?.data ?? res;
          if (d?.nextQuestion) {
            setQuestion(d.nextQuestion);
            setQuizPosition(d.quizPosition || 1);
            setTimeLeft(getCurrentCounter(d.currentExpiryTime));
            isAnswerCorrect.current = false;
          }
        })
        .catch(() => {});
    }
  }, []);

  // Timer
  useEffect(() => {
    if (timeLeft <= 0) {
      if (!submittedRef.current) handleSubmit();
      // Mock: reveal after timeout
      setTimeout(() => {
        setRevealed(true);
      }, 500);
      return;
    }
    const t = setInterval(() => setTimeLeft((p) => Math.max(0, p - 1)), 1000);
    return () => clearInterval(t);
  }, [timeLeft]);

  const handleSelect = (optionId: string) => {
    if (revealed || submittedRef.current) return;
    setSelected((prev) =>
      prev.includes(optionId) ? prev : [...prev, optionId]
    );
    if (selected.length + 1 >= question.answerCount) {
      setTimeout(() => handleSubmit([...selected, optionId]), 100);
    }
  };

  const handleSubmit = async (answers?: string[]) => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    const ids = answers || selected;
    submitQuestion({
      gameId,
      playerId,
      questionId: question.questionId,
      timeTaken: question.timeInterval - timeLeft,
      optionIds: ids,
      optionType: question.optionType,
    } as any)
      .then((res) => {
        setCorrectAnswers(res.data.answers);
        isAnswerCorrect.current = isOverallCorrect(
          question.options,
          ids,
          res.data.answers
        );
        if (isAnswerCorrect.current) setShowConfetti(true);
        if (!scheduledGame) setRevealed(true);
      })
      .catch((error) => {
        toast.error(error.message);
        if (!scheduledGame) setRevealed(true);
      });
  };

  const timerPct = (timeLeft / (question?.timeInterval ?? 60)) * 100;
  const isYou = (currentPlayerId: string) => currentPlayerId === playerId;
  const timerExpired = timeLeft === 0;
  const top3 = leaderboardEntry
    .sort((a, b) => {
      if (b.totalPoints !== a.totalPoints) {
        return b.totalPoints - a.totalPoints;
      }
      return a.totalTime - b.totalTime;
    })
    .slice(0, 3);
  const podiumOrder = top3.length >= 3 ? [top3[1], top3[0], top3[2]] : top3;

  if (gameOver)
    return (
      <PlayerGameOver leaderboard={leaderboardEntry} yourPlayerId={playerId} />
    );

  const getOptionClass = (optionId: string) => {
    if (!revealed)
      return selected.includes(optionId) ? 'pqp__opt--selected' : '';
    const option = question.options.find((o) => o.optionId === optionId);
    const wasSelected = selected.includes(optionId);
    const isCorrect = option && correctAnswers.includes(option.answerText);
    if (wasSelected && isCorrect) return 'pqp__opt--correct';
    if (wasSelected && !isCorrect) return 'pqp__opt--wrong';
    return 'pqp__opt--dim';
  };

  if (!question) return <div className="pqp">Loading...</div>;
  return (
    <div className="pqp">
      <Confetti key={correctAnswers[0]} active={showConfetti} />
      <MusicToggle />
      <div className="pqp__main">
        <div className="pqp__topbar">
          <span>
            Question {quizPosition} of {totalQuestion || 10}
          </span>
        </div>

        <div className="pqp__timer-wrap">
          <svg className="pqp__timer-svg" viewBox="0 0 100 100">
            <circle className="pqp__timer-bg" cx="50" cy="50" r="42" />
            <circle
              className={`pqp__timer-ring ${timerExpired ? 'pqp__timer-ring--expired' : ''}`}
              cx="50"
              cy="50"
              r="42"
              strokeDasharray={264}
              strokeDashoffset={264 - (264 * timerPct) / 100}
            />
          </svg>
          <span className="pqp__timer-text">{timeLeft}</span>
        </div>

        <div className="pqp__question-card">
          <p>{question.questionMessage}</p>
          {question.answerCount > 1 && (
            <span className="pqp__hint">
              Select {question.answerCount} options
            </span>
          )}
        </div>

        <div className="pqp__options">
          {question.options.map((option, i) => (
            <button
              key={option.optionId}
              className={`pqp__opt ${getOptionClass(option.optionId)}`}
              onClick={() => handleSelect(option.optionId)}
              disabled={revealed}
            >
              <span className="pqp__opt-letter">{optionLetters[i]}</span>
              <span>{option.answerText}</span>
            </button>
          ))}
        </div>

        {revealed && quizResultData && (
          <div
            className={`pqp__result-banner ${
              isOverallCorrect(question.options, selected, correctAnswers)
                ? 'pqp__result-banner--correct'
                : 'pqp__result-banner--wrong'
            }`}
          >
            {isOverallCorrect(question.options, selected, correctAnswers)
              ? `✓ Correct!`
              : `✗ Wrong! The answer is ${correctAnswers.join(', ')}`}
          </div>
        )}
      </div>

      {/* Sidebar */}
      <div className="pqp__sidebar">
        <div className="pqp__lb-header">
          <span>🏆</span>
          <h3>Leaderboard</h3>
        </div>

        {revealed && top3.length >= 3 && (
          <div className="pqp__podium">
            {podiumOrder.map((player, i) => {
              const place = podiumOrder.length >= 3 ? [2, 1, 3][i] : i + 1;
              return (
                <div key={player.playerName} className="pqp__podium-col">
                  <span
                    className="pqp__podium-avatar"
                    style={{ background: player.iconColor }}
                  >
                    {player.playerIcon ?? '😊'}
                  </span>
                  <span className="pqp__podium-name">{player.playerName}</span>
                  <div
                    className={`pqp__podium-block pqp__podium-block--${place}`}
                    style={{ background: podiumColors[i] }}
                  >
                    <span className="pqp__podium-place">{place}</span>
                    <span className="pqp__podium-score">
                      {player.totalPoints}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="pqp__lb-list">
          {leaderboardEntry.map((player, rank) => (
            <div
              key={player.playerName}
              className={`pqp__lb-row ${isYou(player.playerId) ? 'pqp__lb-row--you' : ''}`}
            >
              <span className="pqp__lb-rank">#{rank + 1}</span>
              <span
                className="pqp__lb-avatar"
                style={{ background: player.iconColor }}
              >
                {player.playerIcon}
              </span>
              <div className="pqp__lb-info">
                <span className="pqp__lb-name">
                  {player.playerName}{' '}
                  {isYou(player.playerId) && (
                    <span className="pqp__you-badge">You</span>
                  )}
                </span>
                {player.totalPoints > 0 && (
                  <span className="pqp__lb-pts">{player.totalPoints} pts</span>
                )}
              </div>
              <span className="pqp__lb-dash">
                {player.totalPoints > 0 ? player.totalPoints : '—'}
              </span>
            </div>
          ))}
        </div>

        {!revealed && (
          <div className="pqp__answering">
            <span className="pqp__answering-dot" />
            <span>{playersAnswered} players answering</span>
            <div className="pqp__answering-bars">
              {leaderboardEntry.map((player) => (
                <div
                  key={player.playerName}
                  className="pqp__answering-bar"
                  style={{ background: player.iconColor }}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlayerQuizPlay;
